import React from "react";

import TemplateConversationPreview from "../TemplateConversationPreview";
import {
	FormField,
	MessageVariableChips,
	SegmentControl,
} from "./bulkMessagingUi";

const TRIGGER_OPTIONS = [
	{ value: "immediate", label: "Imediato" },
	{ value: "scheduled", label: "Agendado" },
];

function defaultScheduleLocalValue() {
	return new Date().toISOString().split(".")[0].substring(0, 16);
}

function SummaryRow({ label, children }) {
	return (
		<div className="mb-4 last:mb-0">
			<p className="mb-1 text-xs font-semibold uppercase tracking-wide text-default-500 dark:text-default-400">
				{label}
			</p>
			<div className="text-sm text-default-800 dark:text-default-200">{children}</div>
		</div>
	);
}

export default function BulkMessagingReviewStep({
	bulkMessage,
	setBulkMessage,
	bulkMessageId,
	triggerType,
	setTriggerType,
	getDateFormat,
	message,
	darkMode = false,
	tags = [],
}) {
	const previewMessage = message ?? bulkMessage?.bulkMessagingData?.message ?? "";
	const contactsCount = bulkMessage?.contacts?.length ?? 0;
	const selectedTagIds = Array.isArray(bulkMessage?.bulkMessagingData?.tagIds)
		? bulkMessage.bulkMessagingData.tagIds.map((id) => Number(id))
		: [];
	const selectedTagNames = tags
		.filter((tag) => selectedTagIds.includes(Number(tag.id)))
		.map((tag) => tag.name);
	const mediaPath =
		bulkMessage?.bulkMessagingData?.mediaPath || "Nenhum arquivo selecionado";
	const messageInterval = bulkMessage?.bulkMessagingData?.messageInterval ?? 0;
	const clusterSize = bulkMessage?.bulkMessagingData?.clusterSize ?? 0;
	const clusterInterval = bulkMessage?.bulkMessagingData?.clusterInterval ?? 0;
	const channel = String(
		bulkMessage?.bulkMessagingData?.connection?.channel || ""
	).toLowerCase();
	const isWhatsAppCloud =
		channel === "whatsappcloud" ||
		channel === "whatsapp_business_account" ||
		Boolean(
			bulkMessage?.bulkMessagingData?.template ||
				bulkMessage?.bulkMessagingData?.templateId
		);
	const selectedTemplate = bulkMessage?.bulkMessagingData?.template || null;
	const templateParams = Array.isArray(bulkMessage?.bulkMessagingData?.templateParams)
		? bulkMessage.bulkMessagingData.templateParams
		: [];

	const scheduleDefaultValue = bulkMessageId
		? getDateFormat(bulkMessage?.bulkMessagingData?.trigger)
		: defaultScheduleLocalValue();

	const handleTriggerChange = (value) => {
		setTriggerType(value);
		setBulkMessage({
			...bulkMessage,
			bulkMessagingData: {
				...bulkMessage.bulkMessagingData,
				scheduled: value === "scheduled",
			},
		});
	};

	const toInt = (value) => {
		const n = Number.parseInt(String(value), 10);
		return Number.isNaN(n) ? 0 : Math.max(0, n);
	};

	const patchBulkData = (patch) => {
		setBulkMessage({
			...bulkMessage,
			bulkMessagingData: {
				...bulkMessage.bulkMessagingData,
				...patch,
			},
		});
	};

	return (
		<div className="w-full">
			<div className="grid gap-6 md:grid-cols-2 md:gap-8 lg:gap-10">
				<div className="flex flex-col gap-4">
					<FormField id="bulk-trigger-type" className="mb-0">
						<SegmentControl
							options={TRIGGER_OPTIONS}
							value={triggerType}
							onChange={handleTriggerChange}
						/>
					</FormField>

					<FormField id="bulk-schedule-datetime" label="Data do agendamento">
						<input
							id="bulk-schedule-datetime"
							type="datetime-local"
							className="form-input w-full"
							disabled={triggerType === "immediate"}
							defaultValue={scheduleDefaultValue}
							onChange={(e) => {
								try {
									const isoDate = new Date(e.target.value).toISOString();
									patchBulkData({ trigger: isoDate });
								} catch {
									/* invalid date */
								}
							}}
						/>
					</FormField>

					<FormField
						id="bulk-message-interval"
						label="Intervalo entre mensagens (segundos)"
					>
						<input
							id="bulk-message-interval"
							type="number"
							min="0"
							step="1"
							className="form-input w-full"
							value={messageInterval}
							onChange={(e) =>
								patchBulkData({ messageInterval: toInt(e.target.value) })
							}
						/>
					</FormField>

					<FormField
						id="bulk-cluster-interval"
						label="Intervalo entre grupos (leads x segundos)"
					>
						<div className="flex items-center gap-2">
							<input
								id="bulk-cluster-size"
								type="number"
								min="0"
								step="1"
								className="form-input min-w-0 flex-1"
								value={clusterSize}
								onChange={(e) =>
									patchBulkData({ clusterSize: toInt(e.target.value) })
								}
							/>
							<span
								className="shrink-0 text-sm font-medium text-default-500 dark:text-default-400"
								aria-hidden
							>
								x
							</span>
							<input
								id="bulk-cluster-interval-value"
								type="number"
								min="0"
								step="1"
								className="form-input min-w-0 flex-1"
								value={clusterInterval}
								onChange={(e) =>
									patchBulkData({ clusterInterval: toInt(e.target.value) })
								}
							/>
						</div>
					</FormField>
				</div>

				<div className="md:border-s md:border-default-200 md:ps-8 dark:md:border-default-700">
					<SummaryRow label="Nome da campanha:">
						{bulkMessage?.bulkMessagingData?.name}
					</SummaryRow>
					<SummaryRow label="Contatos selecionados:">{contactsCount}</SummaryRow>
					<SummaryRow label="Tags dos contatos:">
						{selectedTagNames.length ? selectedTagNames.join(", ") : "Nenhuma"}
					</SummaryRow>
					{isWhatsAppCloud && selectedTemplate ? (
						<>
							<SummaryRow label="Template:">
								{(selectedTemplate.name || previewMessage || "—").replace(/_/g, " ")}
							</SummaryRow>
							<div className="mb-4">
								<p className="mb-2 text-xs font-semibold uppercase tracking-wide text-default-500 dark:text-default-400">
									Preview do envio
								</p>
								<TemplateConversationPreview
									template={selectedTemplate}
									params={templateParams}
									mediaPath={
										bulkMessage?.bulkMessagingData?.mediaPath || null
									}
									darkMode={darkMode}
								/>
							</div>
						</>
					) : (
						<SummaryRow label="Mensagem:">
							<MessageVariableChips message={previewMessage} />
						</SummaryRow>
					)}
					<SummaryRow label="Arquivo:">{mediaPath}</SummaryRow>
					<SummaryRow label="Tempo de Intervalo:">{messageInterval}</SummaryRow>
					<SummaryRow label="Intervalo entre grupos:">
						{`${clusterSize} x ${clusterInterval}`}
					</SummaryRow>
				</div>
			</div>
		</div>
	);
}
